import * as Accordion from "@radix-ui/react-accordion"
import * as Tabs from "@radix-ui/react-tabs"
import { useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { SeempAction, SeempActionGroup } from "api"
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
	MdChevronLeft,
	MdOutlineHorizontalRule,
	MdOutlineLockPerson,
	MdOutlineTrendingDown,
	MdOutlineTrendingFlat,
	MdOutlineTrendingUp,
} from "react-icons/md"
import SeempDiscussionSection from "./SeempDiscussionSection"
import SeempYtdTrendPlot from "./SeempYtdTrendPlot"

type MeasureTabKey = "investment" | "client dialog" | "crew training"

const isMeasureTabKey = (val: string): val is MeasureTabKey =>
	val === "investment" || val === "client dialog" || val === "crew training"

export type LockedActionList = {
	reportId: number
	actions: { actionId: number; userEmail: string; userName: string }[]
}[]

export default function SeempActionCardPublished({
	lockedActionList,
	plantId,
	reportId,
	actionData,
	usersList,
	displayModeLabel = true,
	handleDeviation,
	actionGroups,
	showDeviation,
	targetYear,
	printMode = false,
	isExpanded = false,
	exposeIsLoaded,
	idKey,
}: {
	lockedActionList: LockedActionList
	plantId: number
	reportId: number
	actionData: SeempAction
	usersList: { email: string; name: string }[]
	displayModeLabel?: boolean
	handleDeviation?: (reportId: number, deviation: number) => void
	actionGroups: SeempActionGroup[]
	showDeviation: boolean
	targetYear: number
	printMode?: boolean
	isExpanded?: boolean
	exposeIsLoaded?: Dispatch<
		SetStateAction<
			{
				idKey: string
				isLoaded: boolean | undefined
			}[]
		>
	>
	idKey?: string
}) {
	const {
		investment,
		clientDialog,
		crewTraining,
		result,
		id: actionId,
		target: targetValue,
		actionType,
	} = actionData

	const userData = useAppSelector(getUserData)
	const companyName = useAppSelector(getCurrentCompany)
	const currentReportActions =
		lockedActionList.find((reportData) => reportData.reportId === reportId)?.actions ?? []
	const { t } = useTranslation()
	const tSeemp = useTranslation("seemp").t

	const [isBeingEditedByOther, setIsBeingEditedByOther] = useState(
		currentReportActions.find((lockedAction) => lockedAction.actionId === actionId)
			?.userEmail !== userData.email
	)
	useEffect(() => {
		setIsBeingEditedByOther(
			currentReportActions.some((item) => item.actionId === actionId) &&
				currentReportActions.find((lockedAction) => lockedAction.actionId === actionId)
					?.userEmail !== userData.email
		)
	}, [lockedActionList])

	const actionIdGroupMap: { type: string[]; label: string }[] = []
	for (const group of actionGroups) {
		const type = group.actionsDefinitions.map((def) => def.type)
		actionIdGroupMap.push({
			type: type,
			label: group.label,
		})
	}

	const actionLabel =
		actionIdGroupMap.find((group) => group.type.includes(actionType))?.label ?? "Unknown"

	const displayName = actionData.actionType.includes(":")
		? tSeemp(
				`${actionLabel}.${actionData.actionType.split(":").slice(1).join(":").trimStart()}`
			)
		: t(actionData.actionType)

	const targetUnit =
		actionGroups
			.find((group) => group.label === actionLabel)
			?.actionsDefinitions.find((action) => action.type === actionType)?.unit ?? ""

	const optimization =
		actionGroups
			.find((group) => group.label === actionLabel)
			?.actionsDefinitions.find((action) => action.type === actionType)?.optimization ?? ""

	const deviation =
		targetValue !== null &&
		result?.targetYear?.value !== null &&
		result?.targetYear?.value !== undefined
			? ((optimization === "maximize" ? -1 : 1) *
					100 *
					(targetValue - result?.targetYear?.value)) /
				targetValue
			: "--"

	useEffect(() => {
		if (handleDeviation === undefined) return
		handleDeviation(actionId, deviation === "--" ? 0 : deviation)
	}, [deviation])

	const description =
		actionGroups
			.find((group) => group.label === actionLabel)
			?.actionsDefinitions.find((action) => action.type === actionType)?.description ?? ""

	const modeDescription =
		actionGroups.find((group) => group.label === actionLabel)?.description ?? ""

	const [selectedMeasuresTab, setSelectedMeasuresTab] = useState<MeasureTabKey>(
		investment.isSelected === true
			? "investment"
			: clientDialog.isSelected === true
				? "client dialog"
				: "crew training"
	)

	const ytdTrend = actionData.result?.targetYear?.trends[0]?.trends

	// EXPOSE LOADING STATE TO REPORT GENERATOR
	useEffect(() => {
		if (exposeIsLoaded === undefined || idKey === undefined) {
			return
		}

		exposeIsLoaded((prev) =>
			prev.map((p) =>
				p.idKey === idKey
					? {
							...p,
							isLoaded: true,
						}
					: p
			)
		)
	}, [exposeIsLoaded])

	const amountMeasures = [
		investment.isSelected,
		clientDialog.isSelected,
		crewTraining.isSelected,
	].filter((m) => m === true).length
	return (
		<div
			className={`${displayModeLabel === true && printMode === false ? "shadow-card" : "border-dipai-secondary-901/20 border-1"} relative bg-white ${printMode === false ? "" : "h-full"}`}
		>
			{companyName === undefined ? (
				<div
					className={`${printMode === false ? "grid-cols-[4.5rem_1fr_4.5rem_4.5rem_4.5rem_4.5rem_4.5rem]" : "grid-cols-[4.5rem_1fr_4.5rem_4.5rem_4.5rem_4.5rem]"} grid w-full gap-3 outline-none`}
				>
					<div className={`flex items-center justify-center`}>
						<MdOutlineTrendingFlat className="h-10 w-10 animate-pulse text-gray-300" />
					</div>
					<div className={`flex h-full flex-col gap-1 py-3 text-left`}>
						<div
							className={`h-[calc(1.5rem-0.5rem/3)] animate-pulse rounded-lg bg-gray-300`}
							style={{ width: (Math.random() * (1 / 5 - 1 / 6) + 1 / 6) * 100 + "%" }}
						/>
						<div
							className={`h-[calc(1.125rem-0.5rem/3)] animate-pulse rounded-lg bg-gray-300`}
							style={{ width: (Math.random() * (4 / 5 - 2 / 5) + 2 / 5) * 100 + "%" }}
						/>
						<div
							className={`h-[calc(1.125rem-0.5rem/3)] animate-pulse rounded-lg bg-gray-300`}
							style={{ width: (Math.random() * (2 / 5 - 1 / 5) + 1 / 5) * 100 + "%" }}
						/>
					</div>
					<div className={`flex flex-col items-center justify-center gap-1`}>
						<label
							className={`text-tiny text-dipai-secondary-901/80 font-medium`}
							htmlFor="last-year-column-loading"
						>
							{t("LAST YEAR")}
						</label>
						<div
							id="last-year-column-loading"
							className={`h-5 w-full animate-pulse rounded-lg bg-gray-300`}
						/>
					</div>
					<div className={`flex flex-col items-center justify-center gap-1`}>
						<label
							className={`text-tiny text-dipai-secondary-901/80 font-medium`}
							htmlFor="target-column-loading"
						>
							{t("TARGET")}
						</label>
						<div
							id="target-column-loading"
							className={`h-5 w-full animate-pulse rounded-lg bg-gray-300`}
						/>
					</div>
					<div className={`flex flex-col items-center justify-center gap-1`}>
						<label
							className={`text-tiny text-dipai-secondary-901/80 font-medium`}
							htmlFor="this-year-column-loading"
						>
							{t("THIS YEAR")}
						</label>
						<div
							id="this-year-column-loading"
							className={`h-5 w-full animate-pulse rounded-lg bg-gray-300`}
						/>
					</div>
					<div className={`flex flex-col items-center justify-center gap-1`}>
						<label
							className={`text-tiny text-dipai-secondary-901/80 font-medium`}
							htmlFor="deviation-column-loading"
						>
							{t("DEVIATION")}
						</label>
						<div
							id="deviation-column-loading"
							className={`h-5 w-full animate-pulse rounded-lg bg-gray-300`}
						/>
					</div>
					{printMode === false ? (
						<div className={`flex items-center justify-center`}>
							<MdChevronLeft
								className="h-10 w-10 rotate-180 animate-pulse text-gray-300 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)]"
								aria-hidden
							/>
						</div>
					) : null}
				</div>
			) : (
				<>
					{isBeingEditedByOther === true ? (
						<div
							className={`border-dipai-secondary-901/20 shadow-card absolute -top-3 -right-3 flex h-7 w-7 items-center justify-center rounded-full border-1 bg-white p-1`}
						>
							<MdOutlineLockPerson
								className="text-dipai-secondary-800 h-5 w-5 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-hover:cursor-pointer"
								aria-hidden
							/>
						</div>
					) : null}

					<Accordion.Item
						value={`${actionId}`}
						className={`border-dipai-secondary-901/20 ${printMode === true ? "h-full" : ""} overflow-hidden rounded-sm ${isBeingEditedByOther === true ? "border-0 border-dashed" : "border-0"}`}
					>
						<Accordion.Header
							className={`flex ${printMode === false ? "min-h-21" : isExpanded === true ? "h-23" : "min-h-full"}`}
						>
							<Accordion.Trigger
								className={`group hover:bg-dipai-secondary-800/5 data-[state=open]:bg-dipai-secondary-800/5 grid w-full ${printMode === false ? "grid-cols-[4.5rem_1fr_4.5rem_4.5rem_4.5rem_4.5rem_4.5rem]" : "grid-cols-[6rem_1fr_6rem_6rem_6rem_6rem_0.75rem]"} gap-3 outline-none`}
							>
								<div className={`flex items-center justify-center`}>
									{showDeviation !== true ? (
										<MdOutlineHorizontalRule
											className={`${printMode === false ? "h-10 w-10" : "h-14 w-14"} text-dipai-secondary-901`}
										/>
									) : deviation === "--" ? (
										<MdOutlineTrendingFlat
											className={`${printMode === false ? "h-10 w-10" : "h-14 w-14"} text-dipai-secondary-901`}
										/>
									) : deviation > 0 ? (
										<MdOutlineTrendingUp
											className={`${printMode === false ? "h-10 w-10" : "h-14 w-14"} text-green-600`}
										/>
									) : deviation < -5 ? (
										<MdOutlineTrendingDown
											className={`${printMode === false ? "h-10 w-10" : "h-14 w-14"} text-red-600`}
										/>
									) : (
										<MdOutlineTrendingFlat
											className={`${printMode === false ? "h-10 w-10" : "h-14 w-14"} text-yellow-600`}
										/>
									)}
								</div>
								<div className={`flex h-full flex-col py-3 text-left`}>
									{displayName !== "custom" ? (
										<p
											className={`${printMode === false ? "text-menu" : "text-body"} text-dipai-secondary-901 font-semibold first-letter:uppercase`}
										>
											{displayModeLabel === true ? `${actionLabel}: ` : null}
											<span className={`font-normal`}>
												{displayName.includes("XX") ? (
													<>
														{displayName
															.split("XX")
															.map((part, idx, arr) => (
																<Fragment key={idx}>
																	{part}
																	{idx < arr.length - 1 && (
																		<span className="font-semibold text-green-600">
																			{actionData.target !==
																			null
																				? actionData.target
																				: "--"}
																		</span>
																	)}
																</Fragment>
															))}
													</>
												) : (
													displayName
												)}
											</span>
										</p>
									) : (
										<span
											className={`${printMode === false ? "text-menu" : "text-body"} text-dipai-text-primary first-letter:uppercase`}
										>{`${actionData.label ?? "unnamed"}`}</span>
									)}
									<p
										className={`${printMode === false ? "text-tiny" : "text-button"} text-dipai-text-primary leading-[1.125rem]`}
									>
										{displayName !== "custom"
											? tSeemp(`${actionLabel}.${description}`)
											: null}
									</p>
									<p
										className={`${printMode === false ? "text-tiny" : "text-button"} text-dipai-text-secondary leading-[1.125rem]`}
									>
										{displayName !== "custom"
											? tSeemp(`${actionLabel}.${modeDescription}`)
											: null}
									</p>
								</div>
								{displayName !== "custom" ? (
									<>
										<div
											className={`flex flex-col items-center justify-center`}
										>
											<label
												className={`${printMode === false ? "text-tiny" : "text-button"} text-dipai-secondary-901/80 font-medium`}
												htmlFor="last-year-column"
											>
												{t("LAST YEAR")}
											</label>
											<p
												id="last-year-column"
												className={`${printMode === false ? "text-menu" : "text-body"} text-dipai-tertiary-700 font-semibold`}
											>
												{`${result?.prevYear?.value.toFixed(2) ?? "--"}${targetUnit === "%" || targetUnit === "" ? "" : " "}${targetUnit}`}
											</p>
										</div>
										<div
											className={`flex flex-col items-center justify-center`}
										>
											<label
												className={`${printMode === false ? "text-tiny" : "text-button"} text-dipai-secondary-901/80 font-medium`}
												htmlFor="target-column"
											>
												{t("TARGET")}
											</label>
											<p
												id="target-column"
												className={`${printMode === false ? "text-menu" : "text-body"} text-dipai-tertiary-700 font-semibold`}
											>
												{`${targetValue ?? "--"}${targetUnit === "%" || targetUnit === "" ? "" : " "}${targetUnit}`}
											</p>
										</div>
										<div
											className={`flex flex-col items-center justify-center`}
										>
											<label
												className={`${printMode === false ? "text-tiny" : "text-button"} text-dipai-secondary-901/80 font-medium`}
												htmlFor="this-year-column"
											>
												{t("THIS YEAR")}
											</label>
											<p
												id="this-year-column"
												className={`${printMode === false ? "text-menu" : "text-body"} text-dipai-tertiary-700 font-semibold`}
											>
												{`${result?.targetYear?.value?.toFixed(2) ?? "--"}${targetUnit === "%" || targetUnit === "" ? "" : " "}${targetUnit}`}
											</p>
										</div>
										<div
											className={`flex flex-col items-center justify-center`}
										>
											<label
												className={`${printMode === false ? "text-tiny" : "text-button"} text-dipai-secondary-901/80 font-medium`}
												htmlFor="deviation-column"
											>
												{t("DEVIATION")}
											</label>
											<p
												id="deviation-column"
												className={`${printMode === false ? "text-menu" : "text-body"} font-semibold ${deviation === "--" ? "text-dipai-secondary-901" : deviation > 0 ? "text-green-600" : deviation < -5 ? "text-red-600" : "text-yellow-600"}`}
											>
												{showDeviation
													? deviation !== "--"
														? `${deviation.toFixed(2)}%`
														: deviation
													: "--"}
											</p>
										</div>
									</>
								) : (
									<div className={`col-span-4`}></div>
								)}
								{printMode === false ? (
									<div className={`flex items-center justify-center`}>
										<MdChevronLeft
											className="text-dipai-secondary-901 group-hover:text-dipai-secondary-600 h-10 w-10 rotate-180 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-hover:scale-110 group-hover:cursor-pointer group-data-[state=open]:rotate-270"
											aria-hidden
										/>
									</div>
								) : null}
							</Accordion.Trigger>
						</Accordion.Header>
						<Accordion.Content
							className={`data-[state=closed]:animate-slide-accordion-up data-[state=open]:animate-slide-accordion-down grid w-full ${printMode === true && isExpanded === true ? "h-[calc(100%-5.75rem)] grid-rows-12" : ""} grid-cols-6 gap-3 overflow-hidden p-3`}
						>
							{/* HOW TO SECTION */}
							<div
								className={`border-dipai-secondary-901/20 text-dipai-secondary-901 ${printMode === false ? (amountMeasures > 0 ? "col-span-3" : "col-span-6") : `col-span-3 col-start-4 ${amountMeasures > 1 ? (amountMeasures > 2 ? "row-span-3" : "row-span-4") : "row-span-6"}`} rounded-sm border-1 p-4`}
							>
								<div
									className={`flex ${printMode === false ? "h-full" : "h-full"} flex-col`}
								>
									<label
										htmlFor="how-to-target"
										className={`text-button text-dipai-secondary-901 h-8 font-semibold`}
									>
										{t("HOW TO REACH PERFORMANCE TARGET")}
									</label>
									<div className={`flex h-[calc(100%-1.5rem)] flex-col`}>
										<p
											id="how-to-target"
											className={`border-dipai-secondary-901/20 text-tiny h-full min-h-13 rounded-sm border-1 p-2`}
										>
											{actionData.howTo ?? t("NOT SPECIFIED")}
										</p>
									</div>
								</div>
							</div>
							{/* MEASURES SECTION */}
							{amountMeasures > 0 ? (
								printMode === true ? (
									<>
										{investment.isSelected === true ? (
											<div
												className={`border-dipai-secondary-901/20 text-dipai-secondary-901 col-span-3 col-start-4 ${amountMeasures > 1 ? (amountMeasures > 2 ? "row-span-3" : "row-span-4") : "row-span-6"} rounded-sm border-1 p-4`}
											>
												<Tabs.Root
													value={`investment`}
													orientation="horizontal"
													className="h-full"
												>
													<Tabs.List className="relative flex w-full flex-row gap-2 overflow-x-hidden">
														{/* INVESTMENT TAB HEADER */}
														<MeasuresSectionTabHeader
															label={t("INVESTMENT")}
															value={`investment`}
															selectedMeasuresTab={
																selectedMeasuresTab
															}
															printMode={printMode}
															deadline={investment.deadline}
															amount={investment.investmentAmount}
															onlyMeasureAvailable={[
																clientDialog.isSelected,
																crewTraining.isSelected,
															].every(
																(selected) => selected === false
															)}
														/>
													</Tabs.List>
													{/* INVESTMENT TAB CONTENT */}
													<MeasuresSectionTabContent
														printMode={printMode}
														value={`investment`}
														howTo={investment.howTo}
														deadline={investment.deadline}
														amount={investment.investmentAmount}
													/>
												</Tabs.Root>
											</div>
										) : null}
										{clientDialog.isSelected === true ? (
											<div
												className={`border-dipai-secondary-901/20 text-dipai-secondary-901 col-span-3 col-start-4 ${amountMeasures > 1 ? (amountMeasures > 2 ? "row-span-3" : "row-span-4") : "row-span-6"} rounded-sm border-1 p-4`}
											>
												<Tabs.Root
													value={`client dialog`}
													orientation="horizontal"
													className="h-full"
												>
													<Tabs.List className="relative flex w-full flex-row gap-2 overflow-x-hidden">
														{/* CLIENT DIALOG TAB HEADER */}
														<MeasuresSectionTabHeader
															label={t("CLIENT DIALOG")}
															value={`client dialog`}
															selectedMeasuresTab={
																selectedMeasuresTab
															}
															printMode={printMode}
															deadline={clientDialog.deadline}
															onlyMeasureAvailable={[
																investment.isSelected,
																crewTraining.isSelected,
															].every(
																(selected) => selected === false
															)}
														/>
													</Tabs.List>
													{/* CLIENT DIALOG TAB CONTENT */}
													<MeasuresSectionTabContent
														printMode={printMode}
														value={`client dialog`}
														howTo={clientDialog.howTo}
														deadline={clientDialog.deadline}
													/>
												</Tabs.Root>
											</div>
										) : null}
										{crewTraining.isSelected === true ? (
											<div
												className={`border-dipai-secondary-901/20 text-dipai-secondary-901 col-span-3 col-start-4 ${amountMeasures > 1 ? (amountMeasures > 2 ? "row-span-3" : "row-span-4") : "row-span-6"} rounded-sm border-1 p-4`}
											>
												<Tabs.Root
													value={`crew training`}
													orientation="horizontal"
													className="h-full"
												>
													<Tabs.List className="relative flex w-full flex-row gap-2 overflow-x-hidden">
														{/* CREW TRAINING TAB HEADER */}
														<MeasuresSectionTabHeader
															label={t("CREW TRAINING")}
															value={`crew training`}
															selectedMeasuresTab={
																selectedMeasuresTab
															}
															printMode={printMode}
															deadline={crewTraining.deadline}
															onlyMeasureAvailable={[
																investment.isSelected,
																clientDialog.isSelected,
															].every(
																(selected) => selected === false
															)}
														/>
													</Tabs.List>
													{/* CREW TRAINING TAB CONTENT */}
													<MeasuresSectionTabContent
														printMode={printMode}
														value={`crew training`}
														howTo={crewTraining.howTo}
														deadline={crewTraining.deadline}
													/>
												</Tabs.Root>
											</div>
										) : null}
									</>
								) : (
									<div
										className={`border-dipai-secondary-901/20 text-dipai-secondary-901 col-span-3 rounded-sm border-1 p-4`}
									>
										{/* MEASURES TABS */}
										<Tabs.Root
											value={selectedMeasuresTab}
											orientation="horizontal"
											onValueChange={(v) => {
												isMeasureTabKey(v) && setSelectedMeasuresTab(v)
											}}
										>
											<Tabs.List className="relative flex w-full flex-row gap-2 overflow-x-hidden">
												{/* INVESTMENT TAB HEADER */}
												{investment.isSelected === true ? (
													<MeasuresSectionTabHeader
														label={t("INVESTMENT")}
														value={`investment`}
														selectedMeasuresTab={selectedMeasuresTab}
														onlyMeasureAvailable={[
															clientDialog.isSelected,
															crewTraining.isSelected,
														].every((selected) => selected === false)}
													/>
												) : null}
												{/* CLIENT DIALOG TAB HEADER */}
												{clientDialog.isSelected === true ? (
													<MeasuresSectionTabHeader
														label={t("CLIENT DIALOG")}
														value={`client dialog`}
														selectedMeasuresTab={selectedMeasuresTab}
														onlyMeasureAvailable={[
															investment.isSelected,
															crewTraining.isSelected,
														].every((selected) => selected === false)}
													/>
												) : null}
												{/* CREW TRAINING TAB HEADER */}
												{crewTraining.isSelected === true ? (
													<MeasuresSectionTabHeader
														label={t("CREW TRAINING")}
														value={`crew training`}
														selectedMeasuresTab={selectedMeasuresTab}
														onlyMeasureAvailable={[
															investment.isSelected,
															clientDialog.isSelected,
														].every((selected) => selected === false)}
													/>
												) : null}
											</Tabs.List>
											<div className={`flex h-[calc(100%-1.5rem)] flex-col`}>
												{/* INVESTMENT TAB CONTENT */}
												<MeasuresSectionTabContent
													printMode={printMode}
													value={`investment`}
													howTo={investment.howTo}
													deadline={investment.deadline}
													amount={investment.investmentAmount}
												/>
												{/* CLIENT DIALOG TAB CONTENT */}
												<MeasuresSectionTabContent
													printMode={printMode}
													value={`client dialog`}
													howTo={clientDialog.howTo}
													deadline={clientDialog.deadline}
												/>
												{/* CREW TRAINING TAB CONTENT */}
												<MeasuresSectionTabContent
													printMode={printMode}
													value={`crew training`}
													howTo={crewTraining.howTo}
													deadline={crewTraining.deadline}
												/>
											</div>
										</Tabs.Root>
									</div>
								)
							) : null}
							{/* PERFORMANCE SECTION */}
							{displayName !== "custom" ? (
								<div
									className={`border-dipai-secondary-901/20 text-dipai-secondary-901 col-span-3 ${printMode === false ? "" : "col-start-1 row-start-1 row-end-11"} rounded-sm border-1 p-4`}
								>
									<p className={`text-button h-8 font-semibold`}>
										{`${t("PERFORMANCE")}`}
										{/* {`${t("PERFORMANCE")} (${t(selectedPlot === "ytd" ? "YTD" : "LAST YEAR")})`} */}
									</p>

									{/* <div className="flex items-center gap-2 mb-2"></div> */}
									<div className={`flex h-[calc(100%-2rem)]`}>
										{/* <SeempTrendPlot
										result={actionData.result}
										target={actionData.target}
										targetUnit={targetUnit}
										plotType="ytd"
									/> */}
										<SeempYtdTrendPlot
											trends={actionData.result?.targetYear}
											target={actionData.target}
											targetUnit={targetUnit}
											targetYear={targetYear}
										/>
									</div>
								</div>
							) : (
								<div
									className={`border-dipai-secondary-901/20 text-dipai-secondary-901 col-span-3 rounded-sm p-4`}
								>
									{" "}
								</div>
							)}
							<div
								className={`col-span-3 ${printMode === false ? "" : "col-start-1 row-start-11 row-end-12"} flex flex-col gap-3`}
							>
								{/* CREATED BY AND RESPONSIBLE PERSON SECTION */}
								<div
									className={`border-dipai-secondary-901/20 grid grid-cols-2 rounded-sm border p-4 px-4.25`}
								>
									<UserRoleBadge
										printMode={printMode}
										role={t("CREATED BY")}
										avatarColorTW={"bg-red-500"}
										userName={
											usersList.find((u) => u.email === actionData.createdBy)
												?.name ?? t("UNKNOWN USER")
										}
										userEmail={actionData.createdBy}
									/>
									<UserRoleBadge
										printMode={printMode}
										role={t("RESPONSIBLE PERSON")}
										avatarColorTW={"bg-purple-500"}
										userName={
											usersList.find(
												(u) => u.email === actionData.responsible
											)?.name ?? t("UNKNOWN USER")
										}
										userEmail={actionData.responsible ?? t("UNKNOWN USER")}
									/>
								</div>
								{/* DISCUSSION SECTION */}
								{printMode === false ? (
									<SeempDiscussionSection
										companyName={companyName}
										plantId={plantId}
										reportId={reportId}
										actionId={actionId}
										comments={actionData.comments ?? []}
										usersList={usersList}
									/>
								) : null}
							</div>
						</Accordion.Content>
					</Accordion.Item>
				</>
			)}
		</div>
	)
}

const UserRoleBadge = ({
	role,
	avatarColorTW,
	userName,
	userEmail,
	printMode = false,
}: {
	role: string
	avatarColorTW: string
	userName: string
	userEmail: string
	printMode?: boolean
}) => {
	return (
		<div className={`flex ${printMode === false ? "flex-col" : "flex-row gap-4"}`}>
			<label
				htmlFor="created-by"
				className={`text-button text-dipai-secondary-901 h-8 font-semibold`}
			>
				{role}
			</label>
			<div className={`flex flex-row items-center gap-2`}>
				<div
					className={`${avatarColorTW} relative inline-block h-6.5 w-6.5 rounded-full border-0 text-center align-middle`}
				>
					<p className="text-tiny absolute top-1/2 left-1/2 -translate-1/2 leading-3 font-bold text-white/90 uppercase">
						{userName.charAt(0).toUpperCase()}
					</p>
				</div>

				<div className={`flex flex-col`}>
					<p
						id="created-by"
						className="text-tiny text-dipai-secondary-901 font-semibold"
					>
						{userName}
					</p>
					<p
						id="created-by"
						className="text-tiny text-dipai-secondary-901/80"
					>
						{userEmail}
					</p>
				</div>
			</div>
		</div>
	)
}

const MeasuresSectionTabHeader = ({
	value,
	label,
	selectedMeasuresTab,
	onlyMeasureAvailable,
	printMode = false,
	deadline,
	amount,
}: {
	value: MeasureTabKey
	label: string
	selectedMeasuresTab: "investment" | "client dialog" | "crew training"
	onlyMeasureAvailable: boolean
	printMode?: boolean
	deadline?: Date | null
	amount?: number | null
}) => {
	const { t } = useTranslation()

	return (
		<Tabs.Trigger
			id={`${value}_tab`}
			value={value}
			className={`border-dipai-secondary-901/20 not-last:border-r-2 not-last:pr-2 ${printMode === true ? "flex w-full items-center justify-between" : ""}`}
			onClick={() => {
				// navigate({
				// 	search: (prev) => ({
				// 		...prev,
				// 		tab: content.name,
				// 	}),
				// })
			}}
		>
			<p
				className={`text-button h-8 cursor-default truncate font-semibold ${
					selectedMeasuresTab === value
						? `text-dipai-secondary-901 ${onlyMeasureAvailable === true ? "no-underline" : "underline"}`
						: `hover:bg-dipai-secondary-901/0 text-dipai-secondary-901/80 hover:text-dipai-secondary-901 relative rounded-md hover:cursor-pointer`
				}`}
			>
				{label}
			</p>
			{printMode === true && (deadline !== undefined || amount !== undefined) ? (
				<div className={`flex flex-row gap-3`}>
					{deadline !== undefined ? (
						<div className={`flex flex-row items-center gap-1.5`}>
							<label
								htmlFor={`${value}-deadline`}
								className={`text-tiny h-4`}
							>
								{t("IMPLEMENTATION DEADLINE")}
							</label>
							<div className={`flex h-full flex-col gap-0.5`}>
								<p
									id={`${value}-deadline`}
									className={`border-dipai-secondary-901/20 text-tiny h-6 rounded-sm border-1 p-0.5`}
								>
									{deadline?.toLocaleDateString() ?? t("NOT SPECIFIED")}
								</p>
							</div>
						</div>
					) : null}
					{amount !== undefined ? (
						<div className={`flex flex-row items-center gap-1.5`}>
							<label
								htmlFor={`${value}-amount`}
								className={`text-tiny h-4`}
							>
								{t("INVESTMENT AMOUNT")}
							</label>
							<div className={`flex h-full flex-col gap-0.5`}>
								<p
									id={`${value}-amount`}
									className={`border-dipai-secondary-901/20 text-tiny h-6 rounded-sm border-1 p-0.5`}
								>
									{amount !== null ? `${amount} €` : t("NOT_SPECIFIED")}
								</p>
							</div>
						</div>
					) : null}
				</div>
			) : null}
		</Tabs.Trigger>
	)
}

const MeasuresSectionTabContent = ({
	value,
	howTo,
	deadline,
	amount,
	printMode = false,
}: {
	value: MeasureTabKey
	howTo: string | null
	deadline: Date | null
	amount?: number | null
	printMode?: boolean
}) => {
	const { t } = useTranslation()
	return (
		<Tabs.Content
			value={value}
			className={"grid h-[calc(100%-2rem)] w-full gap-2.5 overflow-hidden"}
		>
			<div className={`flex h-full w-full flex-col gap-1.5`}>
				{printMode === false ? (
					<label
						htmlFor={`${value}-description`}
						className={`text-tiny h-4`}
					>
						{t("DESCRIPTION")}
					</label>
				) : null}
				<div className={`flex h-full flex-col`}>
					<p
						id={`${value}-description`}
						className={`border-dipai-secondary-901/20 text-tiny ${printMode === true ? "h-full" : "min-h-13"} rounded-sm border-1 p-2`}
					>
						{howTo ?? t("NOT SPECIFIED")}
					</p>
				</div>
			</div>
			{printMode === false ? (
				<div className={`grid grid-cols-2 gap-2.5`}>
					<div className={`flex flex-col gap-1.5`}>
						<label
							htmlFor={`${value}-deadline`}
							className={`text-tiny h-4`}
						>
							{t("IMPLEMENTATION DEADLINE")}
						</label>
						<div className={`flex h-full flex-col gap-0.5`}>
							<p
								id={`${value}-deadline`}
								className={`border-dipai-secondary-901/20 text-tiny h-8 rounded-sm border-1 p-2`}
							>
								{deadline?.toLocaleDateString() ?? t("NOT SPECIFIED")}
							</p>
						</div>
					</div>

					{amount !== undefined ? (
						<div className={`flex flex-col gap-1.5`}>
							<label
								htmlFor={`${value}-amount`}
								className={`text-tiny h-4`}
							>
								{t("INVESTMENT AMOUNT")}
							</label>
							<div className={`flex h-full flex-col gap-0.5`}>
								<p
									id={`${value}-amount`}
									className={`border-dipai-secondary-901/20 text-tiny h-8 rounded-sm border-1 p-2`}
								>
									{amount !== null ? `${amount} €` : t("NOT_SPECIFIED")}
								</p>
							</div>
						</div>
					) : null}
				</div>
			) : null}
		</Tabs.Content>
	)
}
